import { type PolygonIdentifier } from "../types";

import type maplibregl from "maplibre-gl";

export type FeatureState = Record<string, unknown>;

export function buildFeatureIdentifier(
  sourceId: string,
  featureId: string | number,
  sourceLayerId?: string,
): maplibregl.FeatureIdentifier {
  const target: maplibregl.FeatureIdentifier = {
    source: sourceId,
    id: featureId,
  };

  if (sourceLayerId) {
    (target as maplibregl.FeatureIdentifier & { sourceLayer: string }).sourceLayer = sourceLayerId;
  }

  return target;
}

/**
 * Safely retrieves feature state from the map.
 * 
 * Uses `identifier.value` directly as the feature ID for MapLibre's feature state,
 * rather than relying on `feature.id`. This ensures unique feature state management
 * even when multiple features share the same `feature.id` (common with vector tiles).
 * 
 * @param map - The MapLibre map instance
 * @param sourceId - The source ID containing the feature
 * @param identifier - Polygon identifier with key and value
 * @param options - Additional options including sourceLayerId, fallback state, and debug label
 * @returns Object containing the feature state and target identifier, or fallback state if unavailable
 */
export function getFeatureStateSafe(
  map: maplibregl.Map | null | undefined,
  sourceId: string,
  identifier: PolygonIdentifier,
  options: {
    sourceLayerId?: string;
    fallback?: FeatureState;
    debugLabel?: string;
  } = {},
): { featureState: FeatureState; target: maplibregl.FeatureIdentifier | null } {
  const fallback = options.fallback ?? {};

  if (!map || identifier.value == null) return { featureState: fallback, target: null };
  if (!map.isStyleLoaded()) return { featureState: fallback, target: null };

  if (!map.getSource(sourceId)) return { featureState: fallback, target: null };

  try {
    const target = buildFeatureIdentifier(sourceId, identifier.value, options.sourceLayerId);
    return { featureState: map.getFeatureState(target) ?? fallback, target };
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      const label = options.debugLabel ? ` (${  options.debugLabel  })` : "";
      console.warn(`[getFeatureStateSafe] failed${  label  }:`, err);
    }
    return { featureState: fallback, target: null };
  }
}
